import { useFeatureValue } from '@growthbook/growthbook-react';
import {
  AccountCircle,
  Email,
  Login,
  Logout,
  ManageAccounts,
} from '@mui/icons-material';
import { Box, IconButton, Menu, Theme, Typography } from '@mui/material';
import { useMediaQuery } from '@mui/system';
import { skipToken } from '@reduxjs/toolkit/query';
import { useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { Button, ButtonVariants } from '@/components';
import { Modals, NEW_FEATURE_ALERTS, Routes, urls } from '@/constants';
import { useNewFeatureAlert } from '@/hooks';
import { DownloadAppIcon, MembersIcon, ShareIcon } from '@/icons';
import { AnalyticsService, EVENT_CONSTANTS } from '@/services';
import {
  selectRecoveryEmailPending,
  selectSessionUserId,
  selectUserRegion,
  uiActions,
  useAppDispatch,
  useAppSelector,
  useCreatePendingB2BCheckoutMutation,
  useFetchPlanQuery,
  useFetchUserProfileQuery,
  useLazyPollForCheckoutSessionUrlQuery,
  useLogoutMutation,
} from '@/store';
import { appStyles } from '@/theme';
import { PurchaseType } from '@/types';
import { SnackbarUtils } from '@/utils';

export const ProfileMenu = () => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const menuRef = useRef(null);

  const [isOpen, setIsOpen] = useState(false);

  const handleClick = (event: React.MouseEvent) => {
    event.stopPropagation();
    dispatch(uiActions.closeSidebarDrawer());
    setIsOpen(true);
  };

  const handleClose = (event: React.MouseEvent) => {
    event.stopPropagation();
    setIsOpen(false);
  };

  const userId = useAppSelector(selectSessionUserId);
  const { data: plan } = useFetchPlanQuery(userId ? undefined : skipToken);
  const { data: userProfile } = useFetchUserProfileQuery();
  const region = useAppSelector(selectUserRegion);
  const [logout] = useLogoutMutation();
  const [
    createPendingB2BCheckout,
    { isLoading: isCreatingPendingB2BCheckout },
  ] = useCreatePendingB2BCheckoutMutation();
  const [
    pollForCheckoutSessionUrl,
    { isLoading: isCheckoutSessionUrlLoading },
  ] = useLazyPollForCheckoutSessionUrlQuery();
  const isLoading = isCreatingPendingB2BCheckout || isCheckoutSessionUrlLoading;

  const recoveryEmailPending = useAppSelector(selectRecoveryEmailPending);
  const recoveryEmailPrompt = useNewFeatureAlert(
    NEW_FEATURE_ALERTS.PROFILE_RECOVERY_EMAIL_PROMPT,
    { ttl: 7, isEnabled: recoveryEmailPending },
  );

  const isShareWithAFriendEnabled =
    useFeatureValue('share-with-a-friend', false) && !!userId;

  const isDownloadAppNavItemEnabled =
    useFeatureValue('internal-download-app-nav-item', false) && !!userId;

  const magicLoginLinkEnabled =
    useFeatureValue('magic-login-link', false) && !!userId;

  const isMobile = useMediaQuery((theme: Theme) =>
    theme.breakpoints.down('sm'),
  );

  const onUpgradeClick = async () => {
    try {
      const { checkoutSessionTaskId } = await createPendingB2BCheckout({
        countryCode: region,
        organizationId: plan?.organizationId,
        redirectUrl: `${window.location.origin}${Routes.PURCHASE_SUCCESS}`,
      }).unwrap();
      const { url } = await pollForCheckoutSessionUrl({
        checkoutSessionTaskId,
      }).unwrap();
      window.open(url, '_self');
    } catch {
      SnackbarUtils.error('Failed to create team. Please contact support.');
    }
  };

  return (
    <Box sx={{ display: { xs: 'none', sm: 'block' } }}>
      <IconButton
        onClick={handleClick}
        aria-label="Profile menu"
        aria-haspopup="true"
        aria-expanded={isOpen}>
        {userProfile?.firstName || userProfile?.lastName ? (
          <Box
            ref={menuRef}
            sx={{
              bgcolor: 'secondary.main',
              borderRadius: '50%',
              width: 42,
              height: 42,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              border: '2px solid white',
            }}>
            <Typography variant="h4" sx={{ color: 'text.contrast' }}>
              {(userProfile.firstName?.charAt(0) ?? '') +
                (userProfile.lastName?.charAt(0) ?? '')}
            </Typography>
          </Box>
        ) : (
          <AccountCircle sx={styles.appIcon} ref={menuRef} />
        )}
        {recoveryEmailPrompt.isVisible && (
          <Box
            sx={{
              position: 'absolute',
              top: 7,
              right: 7,
              width: 12,
              height: 12,
              bgcolor: 'error.main',
              borderRadius: '50%',
            }}
          />
        )}
      </IconButton>
      <Menu
        anchorEl={menuRef.current}
        keepMounted
        open={isOpen}
        role="menu"
        onClose={handleClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
        slotProps={{
          list: {
            sx: {
              p: 1,
              m: 0,
            },
          },
          paper: {
            sx: {
              mt: 0.5,
              ...appStyles.popoverPaper,
            },
          },
        }}>
        <ProfileMenuItem
          label="Profile"
          Icon={ManageAccounts}
          onClick={() => {
            navigate(Routes.PROFILE);
            setIsOpen(false);
            if (recoveryEmailPrompt.isVisible) {
              recoveryEmailPrompt.dismiss();
            }
          }}
          notificationCount={recoveryEmailPrompt.isVisible ? 1 : 0}
        />
        <ProfileMenuItem
          label="Support"
          Icon={Email}
          onClick={() => {
            window.Intercom?.('show');
            setIsOpen(false);
          }}
        />
        {isShareWithAFriendEnabled && (
          <ProfileMenuItem
            label="Invite friend"
            Icon={ShareIcon}
            onClick={() => {
              dispatch(
                uiActions.setActiveModal({
                  name: Modals.SHARE_WITH_A_FRIEND,
                }),
              );
              setIsOpen(false);
            }}
          />
        )}
        {isDownloadAppNavItemEnabled && (
          <ProfileMenuItem
            label="Download App"
            Icon={DownloadAppIcon}
            onClick={() => {
              AnalyticsService.buttonClick({
                name: EVENT_CONSTANTS.BUTTON.DOWNLOAD_APP,
              });

              if (isMobile) {
                window.open(urls.downloadApp);
                return;
              }

              dispatch(uiActions.setActiveModal({ name: Modals.APP_DOWNLOAD }));
              setIsOpen(false);
            }}
          />
        )}
        {magicLoginLinkEnabled && (
          <ProfileMenuItem
            label="Mobile App"
            Icon={Login}
            onClick={() => {
              dispatch(
                uiActions.setActiveModal({ name: Modals.APP_LOGIN_QR_CODE }),
              );
              setIsOpen(false);
            }}
          />
        )}
        {(plan?.purchaseType === PurchaseType.FREE_TRIAL ||
          plan?.purchaseType === PurchaseType.B2C_PAID ||
          plan?.purchaseType === PurchaseType.TEAM_TRIAL) && (
          <ProfileMenuItem
            label={plan.segment === 'b2c' ? 'Create a Team' : 'Finalize Team'}
            Icon={MembersIcon}
            disabled={isLoading}
            ctaButton={
              <Button
                label="Upgrade"
                variant={ButtonVariants.GRADIENT_PRIMARY}
                fullWidth={false}
                disabled={isLoading}
              />
            }
            onClick={onUpgradeClick}
          />
        )}
        <ProfileMenuItem
          label="Logout"
          Icon={Logout}
          onClick={() => logout()}
        />
      </Menu>
    </Box>
  );
};

const ProfileMenuItem = ({
  onClick,
  label,
  Icon,
  ctaButton,
  notificationCount = 0,
  disabled,
}: {
  onClick: () => void;
  label: string;
  Icon: React.ElementType;
  ctaButton?: React.ReactNode;
  notificationCount?: number;
  disabled?: boolean;
}) => (
  <Box
    sx={{
      p: 1,
      borderRadius: '5px',
      minWidth: 160,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      cursor: disabled ? 'not-allowed' : 'pointer',
      color: 'secondary.main',
      '&:hover': {
        bgcolor: disabled ? 'inherit' : 'purple25',
      },
      gap: 3,
    }}
    component="li"
    role="menuitem"
    tabIndex={0}
    onClick={disabled ? undefined : onClick}
    onKeyUp={e => {
      if (e.key === 'Enter' && !disabled) {
        onClick();
      }
    }}>
    <Box sx={{ display: 'flex', alignItems: 'center' }}>
      <Icon sx={{ mr: 1, width: 24 }} />
      <Typography variant="h6" color="text.main">
        {label}
      </Typography>
    </Box>
    {ctaButton}
    {notificationCount > 0 && (
      <Typography
        sx={{
          bgcolor: 'red',
          color: 'white',
          borderRadius: 9,
          width: 18,
          height: 18,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
        variant="body3SemiBold"
        color="white">
        {notificationCount > 9 ? '9+' : notificationCount}
      </Typography>
    )}
  </Box>
);

const styles = {
  appIcon: {
    color: ({ palette }: Theme) => ({
      xs: palette.header.icon,
      lg: 'grey150',
    }),
    width: 40,
    height: 40,
    px: 0,
  },
};
